import React, { useEffect, useState, useCallback } from "react";
import TopNav from "../layouts/TopNav";
import "../Styles/invoiceTable.css";
import { ToastContainer, toast } from "react-toastify";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate } from "react-router-dom";
import { BsSearch } from "react-icons/bs";
import ReLoginModal from "../components/ReLoginModal";
import { CircularProgress } from "@mui/material";
import SolarMenu from "../asset/svg/solarMenu";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { SidebarModal } from "../components/Modals/sidebarModal";
import LicenseDeleteModal from "../components/LicenseDeleteModal";
import api from "../util/axios";

type License = {
    _id: string;
    licenseNumber: number;
    licenseOwner: string;
    licenseId: string;
    destination: string;
    updatedAt: string;
    issuedDate: string;
    expiryDate: string;
    expired: boolean;
    documentUrl?: string;
};

type ApiError = {
    response?: {
        data?: {
            message?: string;
        };
    };
    message?: string;
};

const LicenseTable = () => {
    const navigate = useNavigate();
    const [reLogin, setReLogin] = useState(false);
    const [getLicense, setGetLicense] = useState<License[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [businessQuery, setBusinessQuery] = useState("");
    const [destinationQuery, setDestinationQuery] = useState("");
    const [searchField, setSearchField] = useState("businessName");
    const [modalDisplay, setModalDisplay] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [licenseImage, setLicenseImage] = useState<string | null>(null);
    const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(null);
    const [formData, setFormData] = useState<{
        licenseNumber: number;
        licenseOwner: string;
        destination: string;
        issuedDate: string;
        expiryDate: string;
    }>({
        licenseNumber: 0,
        licenseOwner: "",
        destination: "",
        issuedDate: "",
        expiryDate: "",
    });

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;

    const userRole = window.localStorage.getItem("userRole");

    const fetchLicenseData = async (pageNumber: number, pageSize: number) => {
        setIsLoading(true);
        const token = window.localStorage.getItem("token");
        if (!token) {

            setReLogin(true);
            setIsLoading(false);
            return;
        }

        const startDate = fromDate || "2023-01-07";
        const endDate = toDate || formattedDate;

        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        };

        try {
            const response = await api.get(
                "/license",
                { headers }
            );
            // console.log("my licenses here:", response);
            // console.log("tracker:", response.data.data.licenses);
            setGetLicense(response.data.data.licenses);
        } catch (error) {
            const err = error as ApiError;
            const errorMessage =
                err.response?.data?.message || err.message || "Failed to fetch licenses";
            if (
                errorMessage.includes("jwt expired") ||
                errorMessage.includes("jwt malformed")
            ) {
                setReLogin(true);
            } else {
                setReLogin(false);
            }
            toast.error(errorMessage || "Failed to fetch data. Please check your internet connection.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLicenseData(page + 1, rowsPerPage);
    }, [page, rowsPerPage]);

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleUpdateLicense = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedLicenseId) {
            toast.error("No license selected for update.");
            return;
        }

        const token = window.localStorage.getItem("token");
        if (!token) {
            setReLogin(true);

            return;
        }

        setIsLoading(true);
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        };

        try {
            // console.log("Sending PATCH request to update license:", selectedLicenseId);
            const response = await api.patch(
                `/license/${selectedLicenseId}`,
                {
                    destination: formData.destination,
                    issuedDate: formData.issuedDate,
                    expiryDate: formData.expiryDate,
                },
                { headers }
            );
            // console.log("Update response:", response.data);
            toast.success("License updated successfully");
            setModalDisplay(false);
            fetchLicenseData(page + 1, rowsPerPage);
        } catch (error) {
            const err = error as ApiError;
            const errorMessage =
                err.response?.data?.message || err.message || "Failed to update license";
            console.error("Update error:", errorMessage);
            if (
                errorMessage.includes("jwt expired") ||
                errorMessage.includes("jwt malformed")
            ) {
                setReLogin(true);
            }
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [formData, selectedLicenseId, page, rowsPerPage]);

    useEffect(() => {
        if (selectedLicenseId && modalDisplay) {
            const license = getLicense.find((lic) => lic._id === selectedLicenseId);
            if (license) {
                setFormData({
                    licenseNumber: license.licenseNumber || 0,
                    licenseOwner: license.licenseOwner || "N/A",
                    destination: license.destination || "",
                    issuedDate: license.issuedDate ? license.issuedDate.split('T')[0] : "",
                    expiryDate: license.expiryDate ? license.expiryDate.split('T')[0] : "",
                });
                setLicenseImage(license.documentUrl || null);
                // console.log("License image found:", license);
            } else {
                toast.error("Selected license not found. Please try again.");
                setModalDisplay(false);
            }

        }
    }, [selectedLicenseId, modalDisplay, getLicense]);

    const filteredLicenses = getLicense?.filter((license: License) => {
        const issuedDate = new Date(license.issuedDate);
        const from = fromDate ? new Date(fromDate) : null;
        const to = toDate ? new Date(toDate) : null;

        const withinDateRange =
            (!from || issuedDate >= from) && (!to || issuedDate <= to);

        const businessNameMatch = license?.licenseOwner
            ?.toLowerCase()
            .includes(searchField === "combined" ? businessQuery.toLowerCase() : searchQuery.toLowerCase());

        const destinationMatch = license?.destination
            ?.toLowerCase()
            .includes(searchField === "combined" ? destinationQuery.toLowerCase() : searchQuery.toLowerCase());

        if (!withinDateRange) return false;

        switch (searchField) {
            case "businessName":
                return businessNameMatch;
            case "destination":
                return destinationMatch;
            case "combined":
                return businessNameMatch && destinationMatch;
            default:
                return businessNameMatch;
        }
    }) || [];


    const handleDeleteLicense = async (id: string) => {
        // console.log("handleDeleteLicense triggered for id:", id);
        const token = window.localStorage.getItem("token");
        if (!token) {
            setReLogin(true);
            return;
        }
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        };
        try {
            // console.log("Sending DELETE request for license:", id);
            await api.delete(
                `/license/${id}`,
                { headers }
            );
            toast.success("License deleted successfully");
            setModalOpen(false);
            fetchLicenseData(page + 1, rowsPerPage);
        } catch (error) {
            const err = error as ApiError;
            const errorMessage =
                err.response?.data?.message || err.message || "Failed to delete license";
            if (
                errorMessage.includes("jwt expired") ||
                errorMessage.includes("jwt malformed")
            ) {
                setReLogin(true);
            } else {
                setReLogin(false);
            }
            toast.error(errorMessage);
        }
    };

    const paginatedLicenses = filteredLicenses.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // console.log("formData:", formData)

    const openImageInNewTab = () => {
        if (licenseImage) {
            window.open(licenseImage, "_blank");
        } else {
            toast.error("No license image available to view.");
        }
    };

    return (
        <div className="board">
            <ToastContainer />
            <TopNav title="Licenses" />
            {reLogin ? <ReLoginModal /> : null}
            <div className="loadingsss">
                <div className="flex justify-between mb-4">
                    <div className="searchFieldSelect mr-4">
                        <label htmlFor="filter">Search filter: </label>
                        <select id="filter" value={searchField} onChange={(e) => setSearchField(e.target.value)}>
                            <option value="businessName">License Owner</option>
                            <option value="destination">Destination</option>
                            <option value="combined">Mixed search</option>
                        </select>
                    </div>
                    {searchField === "combined" ? (
                        <div className="combinedSearch">
                            <input
                                type="text"
                                placeholder="License Owner"
                                value={businessQuery}
                                onChange={(e) => setBusinessQuery(e.target.value)}
                            />
                            <input
                                type="text"
                                placeholder="Destination"
                                value={destinationQuery}
                                onChange={(e) => setDestinationQuery(e.target.value)}
                            />
                        </div>
                    ) : (
                        <div className="apInput">
                            <input
                                type="text"
                                value={searchQuery}
                                placeholder="Search"
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <BsSearch className="apIcon" />
                        </div>
                    )}
                </div>

                <div>
                    <div className="filterBy">
                        <strong>Filter by: </strong>
                        <span>Start date: </span>
                        <input
                            type="date"
                            name="startDate"
                            id=""
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                        <span>End date: </span>
                        <input
                            type="date"
                            name="endDate"
                            id=""
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <div className="legend" style={{ marginTop: "2px", fontSize: "0.9em", color: "#555" }}>
                <p>
                    <span className="expired-badge">Expired</span> indicates expired licenses.
                </p>
            </div>

            <div className="invoiceTable">
                {isLoading ? (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <CircularProgress />
                        <h2>Please wait while we process the request...</h2>
                    </div>
                ) : (
                    <>
                        <table className="licenseTable mx-auto">
                            <thead>
                                <tr className="heTable loT">
                                    <th className="sn">S/N</th>
                                    <th>License Number</th>
                                    <th>License Owner</th>
                                    <th>Destination</th>
                                    <th>Issued Date</th>
                                    <th>Expiry Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedLicenses.length > 0 ? (
                                    paginatedLicenses.map((license: License, inx: number) => {
                                        const formattedIssuedDate = new Date(license.issuedDate).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "short",
                                            day: "2-digit",
                                        });
                                        const formattedExpiryDate = new Date(license.expiryDate).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "short",
                                            day: "2-digit",
                                        });
                                        const expired = license.expired;
                                        return (
                                            <tr key={inx} className={`taData ${expired ? "expiredLicense" : ""}`}>
                                                <td className="sn">{page * rowsPerPage + inx + 1}</td>
                                                <td>
                                                    {license?.licenseNumber || "N/A"}
                                                    {expired && <span className="expired-badge">Expired</span>}
                                                </td>
                                                <td>{license?.licenseOwner || "N/A"}</td>
                                                <td>{license?.destination || "N/A"}</td>
                                                <td>{formattedIssuedDate}</td>
                                                <td>{formattedExpiryDate || "N/A"}</td>
                                                <td>
                                                    <DropdownMenu.Root>
                                                        <DropdownMenu.Trigger asChild>
                                                            <button className="outline-none">
                                                                <SolarMenu />
                                                            </button>
                                                        </DropdownMenu.Trigger>

                                                        <DropdownMenu.Portal>
                                                            <DropdownMenu.Content
                                                                className="bg-white rounded shadow p-2"
                                                                sideOffset={5}
                                                            >
                                                                <DropdownMenu.Item
                                                                    className="px-2 text-blue-500 py-2 rounded hover:bg-gray-100 focus:outline-none cursor-pointer"
                                                                    onSelect={() => {
                                                                        setSelectedLicenseId(license._id);
                                                                        setModalDisplay(true);
                                                                        console.log("View/Edit selected for license:", license._id);
                                                                    }}
                                                                >
                                                                    View / Edit
                                                                </DropdownMenu.Item>
                                                                <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
                                                                {userRole === "admin" ? (
                                                                    <DropdownMenu.Item
                                                                        className="px-2 py-2 rounded hover:bg-red-100 text-red-600 focus:outline-none cursor-pointer"
                                                                        onSelect={() => {
                                                                            console.log("Delete selected for license:", license._id);
                                                                            setSelectedLicenseId(license._id);
                                                                            setModalOpen(true);
                                                                            console.log("Modal state set to open:", true);
                                                                        }}
                                                                    >
                                                                        Delete
                                                                    </DropdownMenu.Item>
                                                                ) : null}
                                                            </DropdownMenu.Content>
                                                        </DropdownMenu.Portal>
                                                    </DropdownMenu.Root>
                                                    <LicenseDeleteModal
                                                        isOpen={modalOpen}
                                                        onClose={() => {
                                                            setModalOpen(false);
                                                        }}
                                                        onConfirm={() => {
                                                            if (selectedLicenseId) handleDeleteLicense(selectedLicenseId);
                                                        }}
                                                        licenseId={selectedLicenseId || ""}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={7} style={{ textAlign: "center" }}>
                                            No data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* View/Edit Modal */}
            <SidebarModal
                title="View / Edit License"
                display={modalDisplay}
                close={() => {
                    setModalDisplay(false);
                    setFormData({
                        licenseNumber: 0,
                        licenseOwner: "",
                        destination: "",
                        issuedDate: "",
                        expiryDate: "",
                    });
                }}
            >
                <div className="license-modal-content" style={{ padding: "20px" }}>
                    <form onSubmit={handleUpdateLicense}>
                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>
                                License Number
                            </label>
                            <input
                                type="text"
                                value={formData.licenseNumber}
                                readOnly
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    backgroundColor: "#f0f0f0",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    cursor: "not-allowed",
                                    outline: "none",
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>
                                License Owner
                            </label>
                            <input
                                type="text"
                                value={formData.licenseOwner}
                                readOnly
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    backgroundColor: "#f0f0f0",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    cursor: "not-allowed",
                                    outline: "none",
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>
                                Destination
                            </label>
                            <input
                                type="text"
                                value={formData.destination}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        destination: e.target.value,
                                    }))
                                }
                                required
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    outline: "none",
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>
                                Issued Date
                            </label>
                            <input
                                type="date"
                                value={formData.issuedDate}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        issuedDate: e.target.value,
                                    }))
                                }
                                required
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    outline: "none",
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: "15px" }}>
                            <label style={{ display: "block", marginBottom: "5px" }}>
                                Expiry Date
                            </label>
                            <input
                                type="date"
                                value={formData.expiryDate}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        expiryDate: e.target.value,
                                    }))
                                }
                                required
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    outline: "none",
                                }}
                            />
                        </div>
                        <div style={{ display: "flex", gap: "10px" }}>
                            <button
                                type="submit"
                                disabled={isLoading}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: isLoading ? "#0B77C5" : "#0B77C5",
                                    color: "#fff",
                                    border: "none",
                                    width: "50%",
                                    borderRadius: "4px",
                                    cursor: isLoading ? "not-allowed" : "pointer",
                                }}
                            >
                                {isLoading ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Update"}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setModalDisplay(false);
                                    setFormData({
                                        licenseNumber: 0,
                                        licenseOwner: "",
                                        destination: "",
                                        issuedDate: "",
                                        expiryDate: "",
                                    });
                                }}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#ccc",
                                    color: "#000",
                                    width: "50%",
                                    border: "none",
                                    borderRadius: "4px",
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                    <div className="mt-6 p-1">
                        {licenseImage ? (
                            <img
                                onClick={openImageInNewTab}
                                src={licenseImage}
                                alt="license document"
                                className="rounded-xl cursor-pointer"
                            />
                        ) : (
                            <p>No license image available</p>
                        )}
                    </div>
                </div>
            </SidebarModal>

            <TablePagination
                rowsPerPageOptions={[15, 25, 50]}
                component="div"
                count={filteredLicenses.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </div>
    );
};

export default LicenseTable;